/**
 * usePushNotifications — React hook to manage Web Push subscription lifecycle.
 *
 * Exposes:
 *   - permission:    'default' | 'granted' | 'denied' | 'unsupported'
 *   - isSubscribed:  bool — whether THIS browser is currently registered with backend
 *   - subscribe():   request permission + subscribe + POST /api/push/subscribe
 *   - unsubscribe(): DELETE /api/push/subscribe + remove browser subscription
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const isSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export default function usePushNotifications(token) {
  const [permission, setPermission] = useState(() =>
    isSupported() ? Notification.permission : 'unsupported'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const checkExistingSubscription = useCallback(async () => {
    if (!isSupported()) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch {}
  }, []);

  useEffect(() => { checkExistingSubscription(); }, [checkExistingSubscription]);

  const subscribe = useCallback(async () => {
    if (!isSupported()) return { error: 'Push not supported in this browser' };
    if (!token) return { error: 'Not logged in' };
    setBusy(true);
    try {
      // 1. Ask for permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setBusy(false);
        return { error: 'Notifications permission denied' };
      }
      // 2. Get VAPID public key from backend
      const keyResp = await axios.get(`${API}/push/vapid-public-key`);
      const publicKey = keyResp.data.public_key;
      if (!publicKey) {
        setBusy(false);
        return { error: 'Server has no VAPID key configured' };
      }
      // 3. Subscribe via Push Manager
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      // 4. Send subscription to backend
      const json = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: json.endpoint,
        keys: json.keys,
      }, { headers });
      setIsSubscribed(true);
      setBusy(false);
      return { ok: true };
    } catch (e) {
      setBusy(false);
      return { error: e.message || 'Subscription failed' };
    }
  }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  const unsubscribe = useCallback(async () => {
    if (!isSupported()) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.delete(`${API}/push/subscribe`, {
          headers,
          data: { endpoint: sub.endpoint },
        }).catch(() => {});
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } catch {}
    setBusy(false);
  }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  return { permission, isSubscribed, busy, subscribe, unsubscribe, isSupported: isSupported() };
}
